// components/GeneratedThumb/hashSeed.ts — deterministic string -> number hash.
//
// Pure. Same input always produces the same output (a plain djb2-style hash),
// which is the whole point: a video's generated thumbnail must look the same
// every time it renders, without any binary asset behind it.
//
// PORTED to mobile/src/core/lib/hashSeed.ts (RN can't apply a CSS hue-rotate
// filter, so mobile rotates the gradient colors in HSL instead — see that file).
// Keep both in sync.

/**
 * Finalizer mix (Murmur3-style) applied to the raw djb2 hash. Plain djb2 on
 * near-identical short strings (our seed ids are literally "vid-01".."vid-12")
 * produces near-sequential outputs — the strings differ by one character whose
 * code point differs by 1, so the hashes differ by exactly 1 too. Every bit
 * this module derives (hue, bar heights) is downstream of `hashSeed`, so
 * without this mixing step the "sequential ids" case never gets real spread no
 * matter which bits a caller slices off afterward.
 */
function mix(x: number): number {
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  return (x ^ (x >>> 16)) >>> 0;
}

export function hashSeed(key: string): number {
  let h = 5381;
  for (let i = 0; i < key.length; i++) {
    h = (h * 33 + key.charCodeAt(i)) >>> 0;
  }
  return mix(h);
}

/** Deterministic pseudo-random bar heights (percent, 18-92) derived from a seed. */
export function barsFromSeed(seed: number, count: number): number[] {
  const bars: number[] = [];
  let s = seed || 1;
  for (let i = 0; i < count; i++) {
    // LCG step — deterministic, no Math.random. Math.imul keeps the intermediate
    // product inside 32-bit range; a plain `s * 1_103_515_245` on a ~2^32 seed
    // exceeds Number.MAX_SAFE_INTEGER (2^53) and silently loses precision.
    s = (Math.imul(s, 1_103_515_245) + 12_345) >>> 0;
    bars.push(18 + (s % 75));
  }
  return bars;
}

/** Deterministic hue-rotate degrees (0-359) so items vary while staying on-brand. */
export function hueFromSeed(seed: number): number {
  return (seed >>> 8) % 360;
}
